import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { getAdminAccountFunnels, getAdminDailyLimits, getAdminWorkUnitsStatus, manageAdminWorkUnit } from 'API/linkedin-funnel';
import AdminSubpageHeader from 'features/admin/admin-subpage-header';
import { Roles } from 'enums';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PaiButton from 'features/ui/base-components/pai-button';
import { useUserData } from 'hooks/use-user-data';
import ReactEcharts from 'echarts-for-react';

interface AdminWorkUnitsStatusResponse {
  timestamp: string;
  totals: { pending: number; processing: number; failed: number; finished: number; interrupted: number };
  pendingBreakdown: {
    byPriority: Record<string, number>;
    byWorktime: { inWorktime: number; outOfWorktime: number; unknown: number };
    byWorkType: Record<string, number>;
  };
  processingSample: Array<{
    id: string; tenantId: string;
    funnelId: string; workType: string; priority: string; inWorktime: boolean; startedAt: string
  }>;
  pendingSample: Array<{
    id: string; tenantId: string;
    funnelId: string; workType: string; priority: string; inWorktime: boolean; createDate: string
  }>;
}

interface AdminDailyLimitsResponse {
  timestamp: string;
  totals: { totalLimits: Record<string, number>; totalUsed: Record<string, number> };
  accounts: Array<{
    tenantId: string;
    tenantName: string;
    accountId: string;
    firstName: string;
    lastName: string;
    userEmail: string;
    status?: string;
    activeFunnelCount?: number;
    limits: Record<string, number | undefined>;
    usedToday: Record<string, number | undefined>;
  }>;
}

const POLL_STATUS_MS = 12000; // 10-15s
const POLL_LIMITS_MS = 90000; // 60-120s

const KPI_ACCENTS: Record<string, string> = {
  pending: '#4F44FF',
  processing: '#007FFF',
  failed: '#F60404',
  finished: '#18C61F',
  interrupted: '#ED6C02',
};

const LIMIT_CHANNELS = ['messages', 'connections', 'emails', 'inmails', 'visits'] as const;

const AdminMonitoring: React.FC = () => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const { token, role } = useUserData();

  const [status, setStatus] = useState<AdminWorkUnitsStatusResponse | null>(null);
  const [limits, setLimits] = useState<AdminDailyLimitsResponse | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [loadingLimits, setLoadingLimits] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; loading: boolean; workItemId?: string; action: 'remove' | 'finish'; }>(
    { open: false, loading: false, action: 'remove' }
  );

  const [expandedAccounts, setExpandedAccounts] = useState<Record<string, boolean>>({});
  const [accountDetails, setAccountDetails] = useState<Record<string, { loading: boolean; error?: string; data?: any }>>({});
  const getAccountKey = (tenantId: string, accountId: string) => `${tenantId}:${accountId}`;

  const fetchStatus = async () => {
    if (!token) return;
    setLoadingStatus(true);
    setError(null);
    try {
      const res = await getAdminWorkUnitsStatus();
      setStatus(res);
    } catch (e: any) {
      setError(e?.message || 'Failed to load status');
    } finally {
      setLoadingStatus(false);
    }
  };

  const fetchLimits = async () => {
    if (!token) return;
    setLoadingLimits(true);
    setError(null);
    try {
      const res = await getAdminDailyLimits();
      setLimits(res);
    } catch (e: any) {
      setError(e?.message || 'Failed to load limits');
    } finally {
      setLoadingLimits(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchLimits();
    const statusInterval = setInterval(fetchStatus, POLL_STATUS_MS);
    const limitsInterval = setInterval(fetchLimits, POLL_LIMITS_MS);
    return () => {
      clearInterval(statusInterval);
      clearInterval(limitsInterval);
    };
  }, [token]);

  const openDeleteDialog = (workItemId: string, action: 'remove' | 'finish' = 'remove') => () => {
    setDeleteDialog({ open: true, loading: false, workItemId, action });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, loading: false, workItemId: undefined, action: 'remove' });
  };

  const confirmDelete = async () => {
    if (!token || !deleteDialog.workItemId) return;
    setDeleteDialog((prev) => ({ ...prev, loading: true }));
    try {
      await manageAdminWorkUnit(deleteDialog.workItemId, deleteDialog.action);
      await fetchStatus();
      closeDeleteDialog();
    } catch (e: any) {
      setError(e?.message || 'Failed to manage work item');
      setDeleteDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const toggleExpandAccount = (tenantId: string, accountId: string) => async () => {
    const key = getAccountKey(tenantId, accountId);
    setExpandedAccounts((prev) => ({ ...prev, [key]: !prev[key] }));
    if (!accountDetails[key]?.data && !accountDetails[key]?.loading) {
      setAccountDetails((prev) => ({ ...prev, [key]: { loading: true } }));
      try {
        const res = await getAdminAccountFunnels(tenantId, accountId);
        setAccountDetails((prev) => ({ ...prev, [key]: { loading: false, data: res } }));
      } catch (e: any) {
        setAccountDetails((prev) => ({ ...prev, [key]: { loading: false, error: e?.message || 'Failed to load account details' } }));
      }
    }
  };

  const pendingByType = useMemo(() => {
    const data = status?.pendingBreakdown?.byWorkType || {};
    const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
    const max = entries.length ? Math.max(...entries.map(([, value]) => value)) : 0;
    return entries.map(([label, value]) => ({
      label,
      value,
      width: max > 0 ? `${Math.round((value / max) * 100)}%` : '0%',
    }));
  }, [status]);

  const pendingByPriority = useMemo(() => {
    const data = status?.pendingBreakdown?.byPriority || {};
    const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
    const max = entries.length ? Math.max(...entries.map(([, value]) => value)) : 0;
    return entries.map(([label, value]) => ({
      label,
      value,
      width: max > 0 ? `${Math.round((value / max) * 100)}%` : '0%',
      color: label === 'high' ? '#F60404' : label === 'normal' ? '#4F44FF' : '#C9CCD3',
    }));
  }, [status]);

  const pendingByWorktimeOption = useMemo(() => {
    const data = status?.pendingBreakdown?.byWorktime || { inWorktime: 0, outOfWorktime: 0, unknown: 0 };
    return {
      grid: { left: 8, right: 8, top: 24, bottom: 8, containLabel: true },
      tooltip: { trigger: 'axis' },
      xAxis: {
        type: 'category',
        data: [t('adminWorktimeIn'), t('adminWorktimeOut'), t('adminWorktimeUnknown')],
        axisLine: { lineStyle: { color: '#E3E3E8' } },
        axisTick: { show: false },
        axisLabel: { color: '#707070', fontSize: 12 },
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: '#F0F0F5' } },
        axisLabel: { color: '#707070', fontSize: 12 },
      },
      series: [
        {
          type: 'bar',
          barMaxWidth: 56,
          data: [data.inWorktime, data.outOfWorktime, data.unknown],
          label: { show: true, position: 'top', color: '#1C1C1C', fontSize: 13 },
          itemStyle: {
            borderRadius: [6, 6, 0, 0],
            color: {
              type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: '#007FFF' },
                { offset: 1, color: '#4F44FF' },
              ],
            },
          },
        },
      ],
    };
  }, [status, t]);

  const utilizationRows = useMemo(() => {
    if (!limits) return [] as Array<any>;
    return limits.accounts.map((acc) => {
      const util: Record<string, number> = {};
      LIMIT_CHANNELS.forEach((ch) => {
        const max = acc.limits[ch] || 0;
        const used = acc.usedToday[ch] || 0;
        util[ch] = max > 0 ? Math.round((used / max) * 100) : 0;
      });
      return { ...acc, util };
    });
  }, [limits]);

  const limitColor = (pct: number) => (pct >= 90 ? '#F60404' : pct >= 80 ? '#ED6C02' : '#4F44FF');

  const formatTime = (value?: string) => {
    if (!value) return '-';
    const date = new Date(value);
    return isNaN(date.getTime()) ? '-' : date.toLocaleTimeString();
  };

  const formatDateTime = (value?: string) => {
    if (!value) return '-';
    const date = new Date(value);
    return isNaN(date.getTime()) ? '-' : date.toLocaleString();
  };

  const priorityChipStyle = (priority: string) => ({
    height: 22,
    fontSize: 12,
    borderRadius: '6px',
    borderColor: priority === 'high' ? '#F60404' : priority === 'normal' ? '#C9CCD3' : '#E3E3E8',
    color: priority === 'high' ? '#F60404' : '#555',
  });

  if (role !== Roles.OWNER) {
    return <Typography p={4}>{t('noPermission', 'You do not have permission to access this page.')}</Typography>;
  }

  return (
    <div className={classes.container}>
      <div className={classes.pageHeader}>
        <AdminSubpageHeader title={t('adminNavMonitoring')} showTitle />
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Typography className={classes.updatedText}>
            {t('adminMonitoringUpdated', {
              time: formatTime(status?.timestamp),
              seconds: Math.round(POLL_STATUS_MS / 1000),
            })}
          </Typography>
          <Button
            variant={'outlined'}
            startIcon={<RefreshIcon />}
            className={classes.refreshButton}
            disabled={loadingStatus || loadingLimits}
            onClick={() => { fetchStatus(); fetchLimits(); }}
          >
            {t('refresh')}
          </Button>
        </Stack>
      </div>

      <div className={classes.content}>
        <Stack gap={2.5} width={'100%'}>
          {error ? <Typography color={'error'}>{error}</Typography> : null}

          {/* KPI band */}
          {loadingStatus && !status ? (
            <LinearProgress />
          ) : (
            <div className={classes.kpiBand}>
              {(['pending', 'processing', 'failed', 'finished', 'interrupted'] as const).map((key) => (
                <div key={key} className={classes.kpiTile} style={{ borderLeftColor: KPI_ACCENTS[key] }}>
                  <Typography className={classes.kpiLabel}>{t(`adminKpi_${key}` as const)}</Typography>
                  <Typography className={classes.kpiValue}>
                    {(status?.totals as any)?.[key]?.toLocaleString() ?? '-'}
                  </Typography>
                </div>
              ))}
            </div>
          )}

          {/* Charts */}
          <div className={classes.chartRow}>
            <div className={classes.card}>
              <Typography className={classes.cardTitle}>{t('adminChartPendingByType')}</Typography>
              <Stack gap={1.25}>
                {pendingByType.length ? pendingByType.map((row) => (
                  <div key={row.label} className={classes.barRow}>
                    <span className={classes.barLabel}>{row.label}</span>
                    <span className={classes.barTrack}>
                      <span className={classes.barFill} style={{ width: row.width, background: '#4F44FF' }} />
                    </span>
                    <span className={classes.barValue}>{row.value}</span>
                  </div>
                )) : <Typography className={classes.emptyText}>{t('adminNoData')}</Typography>}
              </Stack>
            </div>

            <div className={classes.card}>
              <Typography className={classes.cardTitle}>{t('adminChartPendingByPriority')}</Typography>
              <Stack gap={1.25}>
                {pendingByPriority.length ? pendingByPriority.map((row) => (
                  <div key={row.label} className={classes.barRow}>
                    <span className={classes.barLabel}>{row.label}</span>
                    <span className={classes.barTrack}>
                      <span className={classes.barFill} style={{ width: row.width, background: row.color }} />
                    </span>
                    <span className={classes.barValue}>{row.value}</span>
                  </div>
                )) : <Typography className={classes.emptyText}>{t('adminNoData')}</Typography>}
              </Stack>
            </div>

            <div className={classes.card}>
              <Typography className={classes.cardTitle}>{t('adminChartWorktime')}</Typography>
              <ReactEcharts option={pendingByWorktimeOption} style={{ height: 200 }} />
            </div>
          </div>

          {/* Processing live table */}
          <div className={classes.tableCard}>
            <div className={classes.tableHeader}>
              <Typography className={classes.tableTitle}>{t('adminProcessingTitle')}</Typography>
              <span className={classes.countChip}>
                {t('adminProcessingCount', { units: status?.processingSample?.length ?? 0 })}
              </span>
            </div>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.th}>{t('adminColCampaign')}</TableCell>
                    <TableCell className={classes.th}>{t('adminColTenant')}</TableCell>
                    <TableCell className={classes.th}>{t('adminColType')}</TableCell>
                    <TableCell className={classes.th}>{t('adminColPriority')}</TableCell>
                    <TableCell className={classes.th}>{t('adminColStarted')}</TableCell>
                    <TableCell className={cx(classes.th, classes.thRight)} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(status?.processingSample || []).map((row: any) => (
                    <TableRow key={row.id} className={classes.tr}>
                      <TableCell className={classes.td}>{row.funnelName || row.funnelId || '-'}</TableCell>
                      <TableCell className={cx(classes.td, classes.tdMuted)}>{row.tenantId}</TableCell>
                      <TableCell className={classes.td}>
                        <span className={classes.typeChip}>{row.workType}</span>
                      </TableCell>
                      <TableCell className={classes.td}>
                        <Chip size={'small'} variant={'outlined'} label={row.priority} sx={priorityChipStyle(row.priority)} />
                      </TableCell>
                      <TableCell className={cx(classes.td, classes.tdMuted, classes.tdNoWrap)}>
                        {formatDateTime(row.startedAt)}
                      </TableCell>
                      <TableCell className={cx(classes.td, classes.tdRight)}>
                        <span
                          className={classes.removeLink}
                          role={'button'}
                          tabIndex={0}
                          onClick={openDeleteDialog(row.id, 'remove')}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              openDeleteDialog(row.id, 'remove')();
                            }
                          }}
                        >
                          {t('adminActionRemove')}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!status?.processingSample?.length ? (
                    <TableRow>
                      <TableCell className={cx(classes.td, classes.tdMuted)} colSpan={6}>{t('adminNoData')}</TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Pending sample table */}
          <div className={classes.tableCard}>
            <div className={classes.tableHeader}>
              <Typography className={classes.tableTitle}>{t('adminPendingTitle')}</Typography>
              <span className={classes.countChip}>
                {t('adminPendingCount', { units: status?.pendingSample?.length ?? 0 })}
              </span>
            </div>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.th}>{t('adminColCampaign')}</TableCell>
                    <TableCell className={classes.th}>{t('adminColTenant')}</TableCell>
                    <TableCell className={classes.th}>{t('adminColType')}</TableCell>
                    <TableCell className={classes.th}>{t('adminColPriority')}</TableCell>
                    <TableCell className={classes.th}>{t('adminColInWorktime')}</TableCell>
                    <TableCell className={classes.th}>{t('adminColCreated')}</TableCell>
                    <TableCell className={cx(classes.th, classes.thRight)} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(status?.pendingSample || []).map((row: any) => (
                    <TableRow key={row.id} className={classes.tr}>
                      <TableCell className={classes.td}>{row.funnelName || row.funnelId || '-'}</TableCell>
                      <TableCell className={cx(classes.td, classes.tdMuted)}>{row.tenantId}</TableCell>
                      <TableCell className={classes.td}>
                        <span className={classes.typeChip}>{row.workType}</span>
                      </TableCell>
                      <TableCell className={classes.td}>
                        <Chip size={'small'} variant={'outlined'} label={row.priority} sx={priorityChipStyle(row.priority)} />
                      </TableCell>
                      <TableCell className={cx(classes.td, classes.tdMuted)}>
                        {row.inWorktime ? t('yes') : t('no')}
                      </TableCell>
                      <TableCell className={cx(classes.td, classes.tdMuted, classes.tdNoWrap)}>
                        {formatDateTime(row.createDate)}
                      </TableCell>
                      <TableCell className={cx(classes.td, classes.tdRight)}>
                        <span
                          className={classes.removeLink}
                          role={'button'}
                          tabIndex={0}
                          onClick={openDeleteDialog(row.id, 'remove')}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              openDeleteDialog(row.id, 'remove')();
                            }
                          }}
                        >
                          {t('adminActionRemove')}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!status?.pendingSample?.length ? (
                    <TableRow>
                      <TableCell className={cx(classes.td, classes.tdMuted)} colSpan={7}>{t('adminNoData')}</TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Delete Confirmation Dialog */}
          <Dialog open={deleteDialog.open} onClose={closeDeleteDialog} fullWidth maxWidth={'xs'}>
            <DialogTitle>
              <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                <Typography className={classes.tableTitle}>{t('adminConfirmRemovalTitle')}</Typography>
                <IconButton onClick={closeDeleteDialog} size="small"><CloseIcon /></IconButton>
              </Stack>
            </DialogTitle>
            <DialogContent>
              <Typography>{t('adminConfirmRemovalText')}</Typography>
            </DialogContent>
            <DialogActions>
              <PaiButton text={t('cancel')} size={'small'} border={'rounded'} action={closeDeleteDialog} />
              <PaiButton text={t('delete')} size={'small'} border={'rounded'} color={'error'} action={confirmDelete} loading={deleteDialog.loading} />
            </DialogActions>
          </Dialog>

          {/* Daily limits */}
          <div className={classes.tableCard}>
            <div className={classes.tableHeader}>
              <Typography className={classes.tableTitle}>{t('adminDailyLimitsTitle')}</Typography>
            </div>
            {limits ? (
              <div className={classes.limitRow}>
                {LIMIT_CHANNELS.map((ch) => {
                  const total = limits.totals.totalLimits[ch] || 0;
                  const used = limits.totals.totalUsed[ch] || 0;
                  const pct = total > 0 ? Math.round((used / total) * 100) : 0;
                  return (
                    <div key={ch} className={classes.limitItem}>
                      <Typography className={classes.limitLabel}>{t(`adminLimit_${ch}` as const)}</Typography>
                      <span className={classes.limitTrack}>
                        <span className={classes.limitFill} style={{ width: `${Math.min(pct, 100)}%`, background: limitColor(pct) }} />
                      </span>
                      <Typography className={classes.limitText}>{`${used} / ${total} · ${pct} %`}</Typography>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Box p={2}><LinearProgress /></Box>
            )}

            <TableContainer>
              <Table size={'small'} stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.th} />
                    <TableCell className={classes.th}>tenantName</TableCell>
                    <TableCell className={classes.th}>firstName</TableCell>
                    <TableCell className={classes.th}>lastName</TableCell>
                    <TableCell className={classes.th}>userEmail</TableCell>
                    <TableCell className={classes.th}>status</TableCell>
                    <TableCell className={classes.th}>activeCampaigns</TableCell>
                    <TableCell className={classes.th}>limits (msgs/conn/emails/inmails/visits)</TableCell>
                    <TableCell className={classes.th}>usedToday (msgs/conn/emails/inmails/visits)</TableCell>
                    <TableCell className={classes.th}>utilization%</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {utilizationRows.map((row) => (
                    <React.Fragment key={getAccountKey(row.tenantId, row.accountId)}>
                      <TableRow hover className={classes.tr}>
                        <TableCell width={48} className={classes.td}>
                          <IconButton size="small" onClick={toggleExpandAccount(row.tenantId, row.accountId)} aria-label="expand row">
                            {expandedAccounts[getAccountKey(row.tenantId, row.accountId)] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell className={classes.td}>{row.tenantName}</TableCell>
                        <TableCell className={classes.td}>{row.firstName}</TableCell>
                        <TableCell className={classes.td}>{row.lastName}</TableCell>
                        <TableCell className={cx(classes.td, classes.tdMuted)}>{row.userEmail}</TableCell>
                        <TableCell className={classes.td}>{row.status || '-'}</TableCell>
                        <TableCell className={classes.td}>{row.activeFunnelCount ?? '-'}</TableCell>
                        <TableCell className={classes.td}>{`${row.limits.messages || 0}/${row.limits.connections || 0}/${row.limits.emails || 0}/${row.limits.inmails || 0}/${row.limits.visits || 0}`}</TableCell>
                        <TableCell className={classes.td}>{`${row.usedToday.messages || 0}/${row.usedToday.connections || 0}/${row.usedToday.emails || 0}/${row.usedToday.inmails || 0}/${row.usedToday.visits || 0}`}</TableCell>
                        <TableCell className={classes.td}>
                          <Stack direction={'row'} gap={1}>
                            {LIMIT_CHANNELS.map((ch) => (
                              <Chip key={ch} size="small" label={`${ch}: ${row.util[ch]}%`} color={row.util[ch] >= 90 ? 'error' : row.util[ch] >= 80 ? 'warning' : 'default'} />
                            ))}
                          </Stack>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                          <Collapse in={!!expandedAccounts[getAccountKey(row.tenantId, row.accountId)]} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 1 }}>
                              <Typography variant="subtitle2" gutterBottom>Account Details</Typography>
                              {accountDetails[getAccountKey(row.tenantId, row.accountId)]?.loading ? (
                                <LinearProgress />
                              ) : accountDetails[getAccountKey(row.tenantId, row.accountId)]?.error ? (
                                <Typography color="error">{accountDetails[getAccountKey(row.tenantId, row.accountId)]?.error}</Typography>
                              ) : accountDetails[getAccountKey(row.tenantId, row.accountId)]?.data ? (
                                <Stack gap={2}>
                                  {accountDetails[getAccountKey(row.tenantId, row.accountId)]?.data?.performance ? (
                                    <Stack direction={'row'} gap={2}>
                                      <Typography variant="body2" color="text.secondary">
                                        queryTimeMs: {accountDetails[getAccountKey(row.tenantId, row.accountId)].data.performance.queryTimeMs}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        funnels: {accountDetails[getAccountKey(row.tenantId, row.accountId)].data.performance.funnelCount}
                                      </Typography>
                                    </Stack>
                                  ) : null}
                                  <Table size={'small'}>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>funnelName</TableCell>
                                        <TableCell>status</TableCell>
                                        <TableCell>messageApproval</TableCell>
                                        <TableCell>weights (msg/conn/email/inmail/visit)</TableCell>
                                        <TableCell>limits (msg/conn/email/inmail/visit)</TableCell>
                                        <TableCell>usedToday (msg/conn/email/inmail/visit)</TableCell>
                                        <TableCell>contactsPerStep</TableCell>
                                        <TableCell>totalContacts</TableCell>
                                        <TableCell>currentStepIndex</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {(accountDetails[getAccountKey(row.tenantId, row.accountId)].data.funnels || []).map((f: any) => (
                                        <TableRow key={f.funnelId}>
                                          <TableCell>{f.funnelName}</TableCell>
                                          <TableCell>{f.status}</TableCell>
                                          <TableCell>{String(f.messageApproval)}</TableCell>
                                          <TableCell>{`${f.weights?.messages ?? 0}/${f.weights?.connections ?? 0}/${f.weights?.emails ?? 0}/${f.weights?.inmails ?? 0}/${f.weights?.visits ?? 0}`}</TableCell>
                                          <TableCell>{`${f.limits?.messages ?? 0}/${f.limits?.connections ?? 0}/${f.limits?.emails ?? 0}/${f.limits?.inmails ?? 0}/${f.limits?.visits ?? 0}`}</TableCell>
                                          <TableCell>{`${f.usedToday?.messages ?? 0}/${f.usedToday?.connections ?? 0}/${f.usedToday?.emails ?? 0}/${f.usedToday?.inmails ?? 0}/${f.usedToday?.visits ?? 0}`}</TableCell>
                                          <TableCell>{f.contactsPerStep ? Object.entries(f.contactsPerStep).map(([k, v]) => `${k}:${v}`).join(', ') : '-'}</TableCell>
                                          <TableCell>{f.totalContacts ?? '-'}</TableCell>
                                          <TableCell>{f.currentStepIndex ?? '-'}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Stack>
                              ) : (
                                <Typography variant="body2" color="text.secondary">{t('adminNoData')}</Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </Stack>
      </div>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    background: theme.palette.backgrounds.main_new,
    height: 'calc(100vh - 60px - 32px)',
    borderRadius: '6px',
    display: 'flex',
    flexDirection: 'column',
  },
  pageHeader: {
    padding: '24px 32px 20px 32px',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 24,
    flexWrap: 'wrap',
  },
  updatedText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  refreshButton: {
    height: 40,
    padding: '0 18px',
    borderRadius: 8,
    borderColor: theme.palette.greys.lighter,
    background: '#FFF',
    color: theme.palette.greys.dark,
    textTransform: 'none',
    fontSize: theme.typography.pxToRem(14),
    '&:hover': {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      background: '#FFF',
    },
  },
  content: {
    height: '100%',
    padding: '0 32px 32px 32px',
    overflow: 'auto',
    display: 'flex',
  },
  kpiBand: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
  },
  kpiTile: {
    flex: '1 1 160px',
    background: '#FFF',
    borderRadius: 12,
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    borderLeft: '3px solid transparent',
  },
  kpiLabel: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  kpiValue: {
    fontSize: theme.typography.pxToRem(26),
    fontWeight: 500,
    fontVariantNumeric: 'tabular-nums',
  },
  chartRow: {
    display: 'flex',
    gap: 16,
    flexWrap: 'wrap',
  },
  card: {
    flex: '1 1 300px',
    minWidth: 0,
    background: '#FFF',
    borderRadius: 12,
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  cardTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 500,
  },
  barRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  barLabel: {
    width: 120,
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.dark,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  barTrack: {
    flex: 1,
    height: 10,
    background: '#F0F0F5',
    borderRadius: 5,
    overflow: 'hidden',
  },
  barFill: {
    display: 'block',
    height: '100%',
    borderRadius: 5,
  },
  barValue: {
    width: 44,
    textAlign: 'right',
    fontSize: theme.typography.pxToRem(13),
    fontVariantNumeric: 'tabular-nums',
  },
  emptyText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  tableCard: {
    background: '#FFF',
    borderRadius: 12,
    overflow: 'hidden',
  },
  tableHeader: {
    padding: '18px 24px',
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  tableTitle: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 500,
  },
  countChip: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.greys.medium,
    background: theme.palette.backgrounds.main_new,
    borderRadius: 10,
    padding: '3px 8px',
  },
  th: {
    textAlign: 'left',
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 500,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: theme.palette.greys.medium,
    padding: '12px 24px',
    whiteSpace: 'nowrap',
    borderBottom: 'none',
    background: '#FFF',
  },
  thRight: {
    textAlign: 'right',
  },
  tr: {
    borderTop: '1px solid #F0F0F5',
  },
  td: {
    fontSize: theme.typography.pxToRem(14),
    padding: '14px 24px',
    borderBottom: 'none',
    borderTop: '1px solid #F0F0F5',
  },
  tdMuted: {
    color: theme.palette.greys.dark,
  },
  tdNoWrap: {
    whiteSpace: 'nowrap',
  },
  tdRight: {
    textAlign: 'right',
  },
  typeChip: {
    display: 'inline-block',
    fontSize: theme.typography.pxToRem(12),
    background: theme.palette.backgrounds.main,
    color: theme.palette.primary.main,
    borderRadius: 6,
    padding: '4px 8px',
  },
  removeLink: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.error.main,
    cursor: 'pointer',
    textDecoration: 'underline',
    textUnderlineOffset: 3,
  },
  limitRow: {
    padding: '20px 24px',
    display: 'flex',
    gap: 16,
    flexWrap: 'wrap',
  },
  limitItem: {
    flex: '1 1 160px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  limitLabel: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  limitTrack: {
    height: 8,
    background: '#F0F0F5',
    borderRadius: 4,
    overflow: 'hidden',
  },
  limitFill: {
    display: 'block',
    height: '100%',
    borderRadius: 4,
  },
  limitText: {
    fontSize: theme.typography.pxToRem(13),
    fontVariantNumeric: 'tabular-nums',
  },
}));

export default AdminMonitoring;
